import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, AlertCircle } from 'lucide-react';
import { adminApi } from '../api/admin';
import { getErrorMessage } from '../api/client';
import { useAdminAuthStore } from '../store/adminAuthStore';

const SETUP_STORAGE_KEY = 'admin_mfa_setup_token';

/** First admin login until 2FA is enrolled — no JWT until confirmation. */
function MandatoryMfaSetupPage() {
  const navigate = useNavigate();
  const { completeMandatoryMfaAndSignIn, error, clearError } = useAdminAuthStore();

  const [setupToken] = useState(() => sessionStorage.getItem(SETUP_STORAGE_KEY));
  const [qrUri, setQrUri] = useState('');
  const [secret, setSecret] = useState('');
  const [code, setCode] = useState('');
  const [phaseError, setPhaseError] = useState('');
  const [loadingQr, setLoadingQr] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!setupToken) {
      navigate('/login', { replace: true });
      return;
    }

    let cancelled = false;
    (async () => {
      setPhaseError('');
      setLoadingQr(true);
      clearError();
      try {
        const res = await adminApi.beginMandatoryTwoFactorSetup(setupToken);
        if (cancelled) return;
        setQrUri(res.data.qrCodeUri);
        setSecret(res.data.secret);
      } catch (e) {
        if (!cancelled) setPhaseError(getErrorMessage(e, 'Could not start authenticator setup'));
      } finally {
        if (!cancelled) setLoadingQr(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [setupToken, navigate, clearError]);

  const handleConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!setupToken || code.length !== 6) return;
    setPhaseError('');
    clearError();
    setSubmitting(true);
    try {
      await completeMandatoryMfaAndSignIn(setupToken, code);
      navigate('/dashboard', { replace: true });
    } catch {
      /* store sets error */
    } finally {
      setSubmitting(false);
    }
  };

  const displayError = phaseError || error;

  const qrImg =
    qrUri ? `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(qrUri)}` : '';

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md text-center">
        <div className="flex justify-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-indigo-600">
            <Shield className="h-6 w-6 text-white" />
          </div>
        </div>
        <h2 className="mt-4 text-2xl font-bold text-gray-900">Set up two-factor authentication</h2>
        <p className="mt-2 text-sm text-gray-600">
          Admin access requires an authenticator app (e.g. Google Authenticator or Authy).
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-lg">
        <div className="bg-white py-8 px-4 shadow sm:rounded-xl sm:px-8 border border-gray-200">
          {displayError && (
            <div className="rounded-md bg-red-50 p-4 mb-4 flex gap-3">
              <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800">{displayError}</p>
            </div>
          )}

          {loadingQr ? (
            <p className="text-center text-gray-500 py-12">Preparing QR code…</p>
          ) : (
            <>
              {qrImg && (
                <div className="flex justify-center mb-4">
                  <img src={qrImg} alt="2FA setup QR code" className="rounded-lg border border-gray-100" />
                </div>
              )}
              <p className="text-xs text-gray-500 text-center mb-2">
                If scanning fails, enter this secret manually in your authenticator app:
              </p>
              <p className="text-center font-mono text-xs break-all bg-gray-50 border rounded-lg p-3 mb-6">
                {secret}
              </p>

              <form onSubmit={handleConfirm} className="space-y-4">
                <div>
                  <label htmlFor="mfa-code" className="block text-sm font-medium text-gray-700">
                    Verification code from the app (6 digits)
                  </label>
                  <input
                    id="mfa-code"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    value={code}
                    onChange={(ev) => setCode(ev.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-lg tracking-widest text-center font-mono focus:border-indigo-500 focus:ring-indigo-500"
                    placeholder="000000"
                  />
                </div>
                <button
                  type="submit"
                  disabled={submitting || code.length !== 6}
                  className="w-full flex justify-center rounded-md bg-indigo-600 py-2.5 text-sm font-semibold text-white shadow hover:bg-indigo-700 disabled:opacity-50"
                >
                  {submitting ? 'Verifying…' : 'Verify and sign in'}
                </button>
              </form>
            </>
          )}
        </div>
        <p className="mt-6 text-center text-xs text-gray-400">
          This setup session expires after a short time — if it expires, sign in again with email and password.
        </p>
      </div>
    </div>
  );
}

export default MandatoryMfaSetupPage;
